import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Pencil, Loader2, Save, Columns3, Download } from "lucide-react";
import { getContactBatch, replaceContactBatchContacts, ContactBatchContact } from "./api";
import { useToast } from "../../components/ui/toast";
import { PromptModal } from "../../components/ui/prompt-modal";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { ContactBatchExportModal } from "./contact-batch-export-modal";
import { downloadContactBatchExport } from "./contact-batch-export";
import { CopyButton } from "../../components/ui/copy-button";
import { BatchRenameButton } from "./components/BatchRenameButton";

const MAX_ROWS = 200;
const FIXED_KEYS = new Set(["name", "phone", "phone_number", "contact_id", "metadata", "status"]);

type Row = {
  rid: string;
  contact_id?: string;
  name: string;
  phone_number: string;
  meta: Record<string, string>;
};

let ridSeq = 0;
const newRid = () => `r${Date.now().toString(36)}-${(ridSeq++).toString(36)}`;

const emptyRow = (): Row => ({ rid: newRid(), name: "", phone_number: "", meta: {} });

const CELL_INPUT =
  "w-full rounded-md border bg-canvas px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30";

/** Shared wrapper for every header cell: a fixed-height flex row, tall enough for
 *  the inline icon buttons, so all column labels sit on the same line. */
const HEAD_CELL = "flex h-6 items-center leading-none";

type Dialog =
  | { kind: "add-column" }
  | { kind: "rename-column"; key: string }
  | { kind: "delete-column"; key: string }
  | { kind: "discard" }
  | { kind: "export" }
  | null;

/** Union of metadata keys across all contacts, in first-seen order. */
function deriveMetaColumns(contacts: ContactBatchContact[]): string[] {
  const seen: string[] = [];
  const known = new Set<string>();
  for (const c of contacts) {
    for (const key of Object.keys(c.metadata || {})) {
      if (!known.has(key)) {
        known.add(key);
        seen.push(key);
      }
    }
  }
  return seen;
}

interface AutomationContactEditorPageProps {
  /** Where "back" goes and what it's labelled — lets this same editor be
   * mounted under CRM → Batches (for CRM-created batches) as well as
   * Workflows → Upload Contacts, without duplicating the whole page. */
  backHref?: string;
  backLabel?: string;
}

export function AutomationContactEditorPage({ backHref = "/home/automation/contacts", backLabel = "Upload Contacts" }: AutomationContactEditorPageProps) {
  const { batchId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<Row[]>([]);
  const [metaCols, setMetaCols] = useState<string[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const baselineRef = useRef<string>("");

  const snapshot = useMemo(
    () => JSON.stringify({ rows: rows.map(({ rid, ...r }) => r), metaCols }),
    [rows, metaCols],
  );
  const dirty = baselineRef.current !== "" && snapshot !== baselineRef.current;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["contact-batch", batchId],
    queryFn: () => getContactBatch(batchId, 1, MAX_ROWS),
    enabled: !!batchId,
  });

  // Seed local editable state once the batch loads.
  React.useEffect(() => {
    if (!data) return;
    const cols = deriveMetaColumns(data.contacts);
    const seededRows: Row[] = data.contacts.map((c) => ({
      rid: newRid(),
      contact_id: c.contact_id,
      name: c.name || "",
      phone_number: c.phone_number || "",
      meta: cols.reduce<Record<string, string>>((acc, key) => {
        acc[key] = c.metadata?.[key] != null ? String(c.metadata[key]) : "";
        return acc;
      }, {}),
    }));
    setMetaCols(cols);
    setRows(seededRows);
    setTruncated(data.total > data.contacts.length);
    baselineRef.current = JSON.stringify({
      rows: seededRows.map(({ rid, ...r }) => r),
      metaCols: cols,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const idRe = /^[A-Za-z0-9][A-Za-z0-9_.\-/]*$/;
      const generatedRe = /^cont-[0-9a-f]{8}$/;
      const seen = new Set<string>();
      for (const r of rows) {
        const v = (r.contact_id || "").trim();
        if (!v) continue;
        if (v.length > 120 || !idRe.test(v))
          throw new Error(`Contact ID "${v}": use letters/digits/_ . - / only (max 120).`);
        if (generatedRe.test(v))
          throw new Error(`Contact ID "${v}" looks auto-generated — use your own id like cust-001.`);
        const lc = v.toLowerCase();
        if (seen.has(lc)) throw new Error(`Duplicate Contact ID "${v}" — each row needs its own id.`);
        seen.add(lc);
      }
      const contacts = rows
        .map((r) => ({
          contact_id: (r.contact_id || "").trim() || undefined,
          name: r.name.trim() || undefined,
          phone_number: r.phone_number.trim(),
          metadata: metaCols.reduce<Record<string, string>>((acc, key) => {
            const v = (r.meta[key] ?? "").trim();
            if (v) acc[key] = v;
            return acc;
          }, {}),
        }))
        .filter((c) => !!c.phone_number);
      if (contacts.length === 0) throw new Error("Add at least one row with a phone number.");
      return replaceContactBatchContacts(batchId, contacts);
    },
    onSuccess: (res: any) => {
      const dropped = Number(res?.rejected_contacts || 0);
      if (dropped > 0) {
        toast.warning(
          "Saved with rejections",
          `${dropped} row(s) were dropped for an invalid or duplicate phone number.`,
        );
      } else {
        toast.success("Contacts saved");
      }
      queryClient.invalidateQueries({ queryKey: ["contact-batch", batchId] });
      queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      baselineRef.current = snapshot;
    },
    onError: (err: any) => toast.danger("Couldn't save contacts", err.message),
  });

  const goBack = () => {
    if (dirty) {
      setDialog({ kind: "discard" });
      return;
    }
    navigate(backHref);
  };

  const patchRow = (rid: string, patch: Partial<Pick<Row, "name" | "phone_number" | "contact_id">>) =>
    setRows((rs) => rs.map((r) => (r.rid === rid ? { ...r, ...patch } : r)));

  const patchMeta = (rid: string, key: string, value: string) =>
    setRows((rs) => rs.map((r) => (r.rid === rid ? { ...r, meta: { ...r.meta, [key]: value } } : r)));

  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const deleteRow = (rid: string) => setRows((rs) => rs.filter((r) => r.rid !== rid));

  const keyIsTaken = (name: string) => {
    const lc = name.trim().toLowerCase();
    return FIXED_KEYS.has(lc) || metaCols.some((c) => c.toLowerCase() === lc);
  };

  // Returns false (and toasts) when the name collides, so the modal stays open.
  const commitAddColumn = (name: string): boolean => {
    if (keyIsTaken(name)) {
      toast.danger("Column exists", `A column named "${name}" already exists.`);
      return false;
    }
    setMetaCols((cs) => [...cs, name]);
    setRows((rs) => rs.map((r) => ({ ...r, meta: { ...r.meta, [name]: "" } })));
    return true;
  };

  const commitRenameColumn = (key: string, name: string): boolean => {
    if (name === key) return true;
    if (keyIsTaken(name)) {
      toast.danger("Column exists", `A column named "${name}" already exists.`);
      return false;
    }
    setMetaCols((cs) => cs.map((c) => (c === key ? name : c)));
    setRows((rs) =>
      rs.map((r) => {
        const { [key]: value, ...rest } = r.meta;
        return { ...r, meta: { ...rest, [name]: value ?? "" } };
      }),
    );
    return true;
  };

  const commitDeleteColumn = (key: string) => {
    setMetaCols((cs) => cs.filter((c) => c !== key));
    setRows((rs) =>
      rs.map((r) => {
        const { [key]: _drop, ...rest } = r.meta;
        return { ...r, meta: rest };
      }),
    );
  };

  const readyCount = rows.filter((r) => r.phone_number.trim()).length;

  if (isLoading) {
    return <div className="p-16 text-center text-sm text-ink-3">Loading contacts…</div>;
  }
  if (isError || !data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <p className="text-sm text-danger">Couldn't load this contact batch.</p>
        <button onClick={goBack} className="mt-4 text-sm font-semibold text-brand hover:underline">
          Back to {backLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-10 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-3 hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
          </button>
          <div className="flex items-center gap-1.5">
            <h1 className="text-sm font-semibold text-ink">{data.batch.name}</h1>
            <BatchRenameButton batchId={data.batch.batch_id} name={data.batch.name} tags={data.batch.tags || []} />
          </div>
          <p className="mt-1 text-sm text-ink-3">
            <span className="font-mono text-[12px] text-ink-2">{data.batch.batch_id}</span>
            <CopyButton value={data.batch.batch_id} />
            {" · "}
            {readyCount} row{readyCount === 1 ? "" : "s"} with a phone number · {metaCols.length} custom
            column{metaCols.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDialog({ kind: "export" })}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-line bg-elevated px-4 py-2.5 text-sm font-semibold text-ink shadow-sm hover:bg-soft disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !dirty}
            className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-canvas shadow-sm hover:bg-ink-2 disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {truncated && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-2.5 text-xs text-warning">
          This batch has {data.total} rows; only the first {MAX_ROWS} are shown. Saving would replace the
          batch with just the visible rows — split large batches before editing here.
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-line bg-elevated shadow-sm">
        <table className="w-max min-w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-soft text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {/* Every header cell wraps its label in the same fixed-height flex row.
                  Without it, `vertical-align: middle` centres each cell's line box,
                  whose height varies with the icons / button some cells also hold —
                  which is what knocked the added columns off the shared baseline. */}
              <th className="h-11 w-10 whitespace-nowrap px-3 align-middle">
                <div className={HEAD_CELL + " justify-center"}>#</div>
              </th>
              <th className="h-11 min-w-[140px] whitespace-nowrap px-3 align-middle">
                <div className={HEAD_CELL}>Contact ID</div>
              </th>
              <th className="h-11 min-w-[180px] whitespace-nowrap px-3 align-middle">
                <div className={HEAD_CELL}>Name</div>
              </th>
              <th className="h-11 min-w-[180px] whitespace-nowrap px-3 align-middle">
                <div className={HEAD_CELL}>
                  Phone number <span className="text-danger">*</span>
                </div>
              </th>
              {metaCols.map((col) => (
                <th key={col} className="group h-11 min-w-[160px] whitespace-nowrap px-3 align-middle">
                  <div className={HEAD_CELL}>
                    <span>{col}</span>
                    <span className="ml-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() => setDialog({ kind: "rename-column", key: col })}
                        className="rounded p-1 text-ink-3 hover:bg-canvas hover:text-ink"
                        title="Rename column"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => setDialog({ kind: "delete-column", key: col })}
                        className="rounded p-1 text-ink-3 hover:bg-danger-soft hover:text-danger"
                        title="Delete column"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  </div>
                </th>
              ))}
              <th className="h-11 w-32 whitespace-nowrap px-3 align-middle">
                <div className={HEAD_CELL}>
                  <button
                    onClick={() => setDialog({ kind: "add-column" })}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-line px-2 py-1 text-[11px] font-semibold text-ink-3 hover:border-brand hover:text-brand"
                    title="Add column"
                  >
                    <Columns3 className="h-3 w-3" /> Column
                  </button>
                </div>
              </th>
              <th className="h-11 w-10 px-3 align-middle" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row, i) => (
              <tr key={row.rid} className="hover:bg-soft/40">
                <td className="px-3 py-1.5 text-center text-xs text-ink-3">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <input
                    value={row.contact_id || ""}
                    onChange={(e) => patchRow(row.rid, { contact_id: e.target.value })}
                    placeholder="cust-001"
                    title="Your stable id — same id in batch, lead and dial. Leave blank to auto-assign."
                    className={CELL_INPUT + " border-line font-mono text-xs"}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    value={row.name}
                    onChange={(e) => patchRow(row.rid, { name: e.target.value })}
                    placeholder="Optional"
                    className={CELL_INPUT + " border-line"}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    value={row.phone_number}
                    onChange={(e) => patchRow(row.rid, { phone_number: e.target.value })}
                    placeholder="+91 98xxxxxxxx"
                    className={
                      CELL_INPUT +
                      " font-mono " +
                      (row.phone_number.trim() ? "border-line" : "border-danger/60")
                    }
                  />
                </td>
                {metaCols.map((col) => (
                  <td key={col} className="px-2 py-1.5">
                    <input
                      value={row.meta[col] ?? ""}
                      onChange={(e) => patchMeta(row.rid, col, e.target.value)}
                      className={CELL_INPUT + " border-line"}
                    />
                  </td>
                ))}
                <td className="px-3 py-1.5" />
                <td className="px-2 py-1.5 text-center">
                  <button
                    onClick={() => deleteRow(row.rid)}
                    className="rounded-md p-1.5 text-ink-3 hover:bg-danger-soft hover:text-danger"
                    title="Delete row"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={metaCols.length + 6} className="px-3 py-10 text-center text-sm text-ink-3">
                  No rows. Add one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <button
          onClick={addRow}
          className="flex w-full items-center justify-center gap-1.5 border-t border-line bg-soft/40 py-2.5 text-xs font-semibold text-ink-2 hover:bg-soft"
        >
          <Plus className="h-3.5 w-3.5" /> Add row
        </button>
      </div>

      <p className="text-xs text-ink-3">
        Rows without a phone number are ignored on save. Invalid or duplicate phone numbers are dropped and
        reported after saving. Contact ID is your stable id (same in batch, lead and dial) — leave blank to
        auto-assign.
      </p>

      {dialog?.kind === "add-column" && (
        <PromptModal
          title="Add column"
          label="Column name"
          placeholder="e.g. city"
          submitLabel="Add column"
          onClose={() => setDialog(null)}
          onSubmit={(name) => {
            if (commitAddColumn(name)) setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "rename-column" && (
        <PromptModal
          title={`Rename column "${dialog.key}"`}
          label="New name"
          initial={dialog.key}
          submitLabel="Rename"
          onClose={() => setDialog(null)}
          onSubmit={(name) => {
            if (dialog.kind === "rename-column" && commitRenameColumn(dialog.key, name)) setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "delete-column" && (
        <ConfirmModal
          title="Delete column"
          body={`Delete column "${dialog.key}"? Values in this column will be lost.`}
          confirmLabel="Delete column"
          onClose={() => setDialog(null)}
          onConfirm={() => {
            if (dialog.kind === "delete-column") commitDeleteColumn(dialog.key);
            setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "discard" && (
        <ConfirmModal
          title="Discard changes"
          body="You have unsaved changes. Leave without saving?"
          confirmLabel="Discard"
          onClose={() => setDialog(null)}
          onConfirm={() => navigate(backHref)}
        />
      )}

      {dialog?.kind === "export" && (
        <ContactBatchExportModal
          batchName={data.batch.name}
          rowCount={rows.length}
          truncatedNote={
            truncated
              ? `${data.batch.name} · first ${rows.length} of ${data.total} rows (what's shown in the grid)`
              : null
          }
          onClose={() => setDialog(null)}
          onExport={async (format) => {
            const filename = await downloadContactBatchExport({
              format,
              batchName: data.batch.name,
              metaCols,
              rows,
            });
            toast.success("Export downloaded", filename);
          }}
        />
      )}
    </div>
  );
}
